import React from 'react';
import { jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { BANNER_NAME as BANNER_NAME_COOKIE_CONSENT } from '@jira/platform__banners-cookie-consent/src/bannerName.constants.tsx';
import { CookieConsentBannerAsync as CookieConsentBanner } from '@jira/platform__banners-cookie-consent/src/CookieConsentBannerAsync.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { BANNER_NAME as BANNER_NAME_IPBLOCKLISTING } from '@jira/platform__ip-block-listing-banner/src/common/constants.tsx';
import { IPBlocklistingBannerAsync as BannerIPBlocklisting } from '@jira/platform__ip-block-listing-banner/src/utils.tsx';
import { Banner as AkBanner } from '@jira/design-system__navigation-system/src/PageLayout.tsx';
import { useBanner } from '@jira/platform__controllers-global-banner/src/controllers/index.tsx';
import { NAME as BANNER_NAME_ANNOUNCEMENT } from '@jira/platform__banners-announcement/src/name.constants.tsx';
import { BannerAnnouncementAsync as BannerAnnouncement } from '@jira/platform__banners-announcement/src/BannerAnnouncementAsync.tsx';
import { BANNER_NAME as BANNER_NAME_TRANSITION } from '@jira/platform__banners-eoc-transition/src/bannerName.constants.tsx';
import { BannerTransitionAsync } from '@jira/platform__banners-eoc-transition/src/BannerTransitionAsync.tsx';
import { BANNER_NAME as BANNER_NAME_MAIL_EXPIRY } from '@jira/platform__banners-mail-expiry-reminder/src/bannerName.constants.tsx';
import { MailExpiryBannerAsync as MailExpiryBanner } from '@jira/platform__banners-mail-expiry-reminder/src/MailExpiryBannerAsync.tsx';
import { BANNER_NAME as BANNER_NAME_RETURN_TO_MOBILE } from '@jira/platform__return-to-mobile-banner/src/common/constants.tsx';
import { ReturnToMobileBanner as BannerReturnToMobile } from '@jira/platform__return-to-mobile-banner/src/utils.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { BANNER_NAME as BANNER_NAME_JPD_BLUEPRINTS_FEEDBACK } from '@jira/platform__banners-jpd-blueprints-feedback/src/bannerName.constants.tsx';
import { AsyncJpdBlueprintsFeedbackBanner as JpdBlueprintsFeedbackBanner } from '@jira/platform__banners-jpd-blueprints-feedback/src/AsyncJpdBlueprintsFeedbackBanner.tsx';

import { Cohort3BannerAsync } from '@jira/platform__banners-eoc-cohort3-customers-banner/src/Cohort3BannerAsync.tsx';
import { COHORT3_CONSOLIDATION_BANNER } from '@jira/platform__banners-eoc-cohort3-customers-banner/src/cohort3ConsolidationBanner.constants.tsx';
import type { BannerContent$key } from './__generated__/BannerContent.graphql';

/**
 * This ID is used in pollinator tests and by JPD. Do not change lightly.
 */
const BANNER_ID = 'page-layout.banner';

/**
 * Dynamically displays a banner based on the current context and feature flags. It supports several types of banners,
 * each serving a different purpose (e.g., announcements, cookie consent). The decision to display a particular banner is
 * made through a combination of fetched banner data and the evaluation of feature flags, ensuring that the presented banner
 * is relevant to the user's current interaction and system state.
 *
 *  ALL BANNER COMPONENTS MUST BE PACKAGES THAT ARE LOCATED IN:
 * jira/src/packages/platform/ui/banners
 *
 * Create new packages in this workspace. There is a ratchet rule that depends on this.
 *
 * Please make sure that entryFsSsrLiftImportToModule is set to true for any async banners
 */
export const BannerContent = ({
	bannerFragment,
}: {
	bannerFragment?: BannerContent$key | null;
}): React.JSX.Element | null => {
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment<BannerContent$key>(
		graphql`
			fragment BannerContent on Query
			@argumentDefinitions(
				bannerAnnouncementViaSpa: {
					type: "Boolean!"
					provider: "@jira/platform__graphql-relay-provider/src/banner-announcement-via-spa.relayprovider"
				}
			) {
				jira {
					# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
					...BannerV2BannerAnnouncement
						@dangerously_unaliased_fixme
						@include(if: $bannerAnnouncementViaSpa)
				}
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		bannerFragment,
	);
	const banner = useBanner();
	const bannerName = banner.name;

	return banner.isEnabled ? (
		<UFOSegment name="banner">
			<AkBanner height={banner.height} testId={BANNER_ID} id={BANNER_ID}>
				{bannerName === BANNER_NAME_ANNOUNCEMENT && (
					<BannerAnnouncement jiraQueryFragment={data?.jira} />
				)}
				{bannerName === BANNER_NAME_IPBLOCKLISTING && <BannerIPBlocklisting />}
				{bannerName === BANNER_NAME_RETURN_TO_MOBILE && <BannerReturnToMobile />}
				{bannerName === BANNER_NAME_COOKIE_CONSENT && <CookieConsentBanner />}
				{bannerName === BANNER_NAME_TRANSITION && <BannerTransitionAsync />}
				{bannerName === BANNER_NAME_MAIL_EXPIRY && <MailExpiryBanner />}
				{bannerName === BANNER_NAME_JPD_BLUEPRINTS_FEEDBACK &&
					fg('jpd-view-templates-feedback-banner') && <JpdBlueprintsFeedbackBanner />}
				{bannerName === COHORT3_CONSOLIDATION_BANNER && fg('eoc_consolidation_cohort_3_banner') && (
					<Cohort3BannerAsync />
				)}
			</AkBanner>
		</UFOSegment>
	) : null;
};
